import React from "react"
import Code from "./components/Code"
import PropSelector from "./components/PropSelector"
import styles from "./App.module.scss"
import deepmerge from "deepmerge"
import qs from "query-string"
import PhonePreview from "./components/PhonePreview"
import inputTypeData from "./config/inputTypes"
import inputmodeData from "./config/inputModes"

const reducer = (s, a) => {
  const result = deepmerge(s, a)

  const { accordionOpenIndex, ...query } = result

  const newURL = new window.URL(window.location)
  newURL.search = qs.stringify(query)

  window.history.pushState({}, "", newURL.toString())

  return result
}

const createCodeSnippet = ({ type, inputmode, autocomplete }) => {
  return `
<input
  type="${type}"${inputmode ? `\n  inputmode="${inputmode}"` : ""}${
    autocomplete ? `\n  autocomplete="${autocomplete}"` : ""
  }
/>
`
}

const type = "text"

const defaultConfig = {
  type,
  autocomplete: "one-time-code",
  inputmode: "decimal",
  accordionOpenIndex: 0,
  android: false,
}

const App = () => {
  const [state, setState] = React.useReducer(reducer, defaultConfig)

  React.useEffect(() => {
    if (typeof window === `undefined`) return
    const query = qs.parse(window.location.search)
    // if no query args, show default config
    if (Object.keys(query).length === 0) return
    if (Object.values(query).every(v => v === "undefined")) return
    const vals = {}
    ;["type", "autocomplete", "inputmode"].forEach(val => {
      if (query[val] === "undefined") return
      vals[val] = query[val]
    })
    if (query.android && query.android === "true") vals.android = true
    else vals.android = false
    setState(vals)
  }, [])
  const updateAccordion = index => {
    if (index === state.accordionOpenIndex)
      return setState({
        accordionOpenIndex: null,
      })
    setState({
      accordionOpenIndex: index,
    })
  }

  const html = createCodeSnippet(state)

  const keyboardData =
    state.inputmode &&
    !(inputTypeData[state.type] && inputTypeData[state.type].noInputmode) &&
    inputmodeData[state.inputmode]
      ? inputmodeData[state.inputmode].keyboard
      : inputTypeData[state.type]
      ? inputTypeData[state.type].keyboard
      : inputTypeData.text.keyboard

  return (
    <div className={styles.container}>
      <div>
        <div>
          <h1 className={styles.title}>Build a Better Mobile Input</h1>
          <div className={styles.attribution}>
            by <a href="https://github.com/aholachek">@aholachek</a>
          </div>
          <p style={{ marginBottom: "1rem" }}>
            By properly configuring a few key input attributes, you can make web
            forms much easier to use on mobile devices.
          </p>
          <p style={{ marginBottom: "1rem" }} className={styles.instructions}>
            Experiment with different combinations of the related{" "}
            <code className="prop">type</code> and{" "}
            <code className="prop">inputmode</code> attributes, and make sure to
            specify an <code className="prop">autocomplete</code> attribute for
            an optimized user experience.
          </p>
          <Code {...state} html={html} setConfig={setState} />

          <PropSelector
            setConfig={setState}
            type={state.type}
            inputmode={state.inputmode}
            html={html}
            autocomplete={state.autocomplete}
            updateAccordion={updateAccordion}
            accordionOpenIndex={state.accordionOpenIndex}
          />
        </div>
      </div>
      <div className={styles.widgets}>
        <PhonePreview
          android={state.android}
          setAndroid={bool => {
            setState({
              android: bool,
            })
          }}
          html={html}
          keyboard={keyboardData}
          updateAccordion={updateAccordion}
          autocomplete={state.autocomplete}
        />
      </div>
    </div>
  )
}

export default App
